import { DEFAULT_SCHEDULED_START_TIME, MILLIS } from '@repo/constants'
import { format } from 'date-fns'
import { DateTime } from 'luxon'

// Combine an ISO date (YYYY-MM-DD) and a time (HH:MM[:SS]) into a local Date.
// Returns null if the date is missing or malformed. Time defaults to 7:00 AM
// when omitted (matches the dispatch creation default).
export function combineScheduledDateTime(
  date: string | null | undefined,
  time: string | null | undefined,
): Date | null {
  if (!date) return null
  const [y, m, d] = date.split('-').map(Number)
  if (!y || !m || !d) return null
  const [hStr, minStr] = (time ?? DEFAULT_SCHEDULED_START_TIME).split(':')
  const h = Number(hStr)
  const min = Number(minStr)
  return new Date(y, m - 1, d, Number.isFinite(h) ? h : 7, Number.isFinite(min) ? min : 0)
}

// Local-timezone YYYY-MM-DD (unlike toISOString, which shifts through UTC
// midnight and can land on the wrong day).
export function toIsoLocalDate(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

// A bare calendar day is read as LOCAL midnight, not UTC midnight. `new
// Date('2026-07-20')` is UTC per spec while `format` renders local wall-clock, so
// west of UTC the pair silently moves the day backwards: a ticket dated 07/20
// prefills as 07/19 07:00 PM in America/Chicago, and accepting that prefill
// records the haul on the wrong calendar day. Same asymmetry that
// ticketConsistency.ts's `toDate` documents — and AI-extracted `hints.ticketDate`
// arrives in exactly this shape, since the prompt pins the model to ISO
// YYYY-MM-DD.
export const toLocalDate = (value: Date | string): Date | null => {
  if (value instanceof Date) return value
  const dateOnly = DATE_ONLY.exec(value)
  if (!dateOnly) return new Date(value)
  return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
}

// `YYYY-MM-DDTHH:MM` in local time — the only format `<input
// type="datetime-local">` accepts. Same reason as toIsoLocalDate: rendering
// through UTC would show the wrong wall-clock time to the reviewer.
export function toDatetimeLocalValue(value: Date | string | null | undefined): string | null {
  if (!value) return null
  const date = toLocalDate(value)
  if (!date || Number.isNaN(date.getTime())) return null
  return format(date, "yyyy-MM-dd'T'HH:mm")
}

// Build the wall-clock ISO timestamp string (no timezone) stored in the
// `scheduled_at` column from the form's date + time pickers.
export function toScheduledAtIso(date: string, time: string | null | undefined): string {
  const t = time ?? DEFAULT_SCHEDULED_START_TIME
  const withSeconds = /^\d{2}:\d{2}$/.test(t) ? `${t}:00` : t
  return `${date}T${withSeconds}`
}

// Split a `scheduled_at` value (whether ISO `YYYY-MM-DDTHH:MM:SS` or PG
// `YYYY-MM-DD HH:MM:SS`) back into the date + time portions used by the form.
export function splitScheduledAt(iso: string): { date: string; time: string } {
  const normalized = iso.replace(' ', 'T')
  const [datePart, timePart] = normalized.split('T')
  const date = datePart ?? ''
  const time = (timePart ?? DEFAULT_SCHEDULED_START_TIME).slice(0, 8)
  return { date, time: time.length === 5 ? `${time}:00` : time }
}

// The UTC instants bounding *today* in the given IANA timezone, for comparing
// against UTC timestamp columns. Null when the zone string is invalid.
export function getUtcRangeForToday(timeZone: string): { start: Date; end: Date } | null {
  const today = getTodayInZone(timeZone)
  return today && { start: today.start, end: today.end }
}

// "Today" in the given IANA timezone in both shapes the schema stores time in:
// the calendar dates bounding a wall-clock `timestamp without time zone` that
// is written in local time (`new_dispatches.scheduled_at`), and the UTC
// instants bounding a column written in UTC. Null when the zone is invalid.
//
// The day is advanced in the local zone before the UTC conversion: adding 24h
// in UTC drifts an hour across DST transitions, where a local day is 23h or 25h.
export function getTodayInZone(
  timeZone: string,
): { date: string; nextDate: string; start: Date; end: Date } | null {
  const startLocal = DateTime.now().setZone(timeZone).startOf('day')
  if (!startLocal.isValid) return null

  const endLocal = startLocal.plus({ days: 1 })
  return {
    date: startLocal.toISODate() ?? '',
    nextDate: endLocal.toISODate() ?? '',
    start: startLocal.toJSDate(),
    end: endLocal.toJSDate(),
  }
}

/** The calendar day `date` falls on in `timeZone`, as `YYYY-MM-DD`; null for an invalid zone. */
export function toIsoDateInZone(date: Date, timeZone: string): string | null {
  return DateTime.fromJSDate(date).setZone(timeZone).toISODate()
}

// The instant a calendar day ends in `timeZone`. Deadlines that are
// communicated to a person as a bare date ("respond by September 16") have to
// stay valid for all of that date; stored as a raw `now + N days` they instead
// inherit the time-of-day they were created at and cut the final day short.
// Returns the input unchanged when the zone string is invalid.
export function endOfDayInZone(date: Date, timeZone: string): Date {
  const local = DateTime.fromJSDate(date).setZone(timeZone)
  if (!local.isValid) return date
  return local.endOf('day').toJSDate()
}

export function getUtcRangeForDate(localDate: string, timeZone: string) {
  // Add the day in the local zone (calendar-aware) before converting to UTC:
  // adding 24h after .toUTC() drifts an hour across DST transitions, where the
  // local day is 23h or 25h long.
  const startLocal = DateTime.fromISO(`${localDate}T00:00:00`, { zone: timeZone })
  const endLocal = startLocal.plus({ days: 1 })

  return {
    start: startLocal.toUTC().toISO(),
    end: endLocal.toUTC().toISO(),
  }
}

// Whole years elapsed between a `YYYY-MM-DD` date of birth and `at`. Returns
// null for a missing or unparseable date so callers can decide what an unknown
// age means rather than silently getting 0.
//
// Both sides are read in UTC. A `date` column carries no timezone, so there is
// no correct local zone to interpret it in — and using the server's would make
// the answer depend on where the process happens to run, which is not something
// a compliance threshold should hinge on. (This is also why date-fns'
// `differenceInYears` is the wrong tool: it reads local calendar parts.)
export function ageInYearsAt(dateOfBirth: string | null | undefined, at: Date): number | null {
  if (!dateOfBirth) return null
  const born = DateTime.fromISO(dateOfBirth, { zone: 'utc' })
  if (!born.isValid) return null
  return Math.floor(DateTime.fromJSDate(at, { zone: 'utc' }).diff(born, 'years').years)
}

// Calendar-day arithmetic on bare `YYYY-MM-DD` dates (a Postgres `date`), done in
// UTC so no runtime zone or DST shift can move the day.
const isoDateAsUtc = (isoDate: string) => Date.parse(`${isoDate}T00:00:00Z`)

/** `isoDate` moved by `days` calendar days. */
export const addDaysToIsoDate = (isoDate: string, days: number): string =>
  new Date(isoDateAsUtc(isoDate) + days * MILLIS.DAY).toISOString().slice(0, 10)

/** Whole calendar days from `from` to `to`; negative when `to` is earlier. */
export const daysBetweenIsoDates = (from: string, to: string): number =>
  Math.round((isoDateAsUtc(to) - isoDateAsUtc(from)) / MILLIS.DAY)
